// An instructor's private note on one student (IW-5). Save stores it through the author-only
// route; saving it empty deletes it. Only the instructor who wrote it ever reads it back.
import React from 'react';
import { Btn } from './index.jsx';
import { api } from '../lib/api';
import { palette, type } from '../design';

const FONT = "'Plus Jakarta Sans', sans-serif";

// Mirrors INSTRUCTOR_NOTE_MAX_LENGTH in backend/src/lib/constants.js; the server enforces it.
export const NOTE_MAX_LENGTH = 2000;
const MAX_LABEL = NOTE_MAX_LENGTH.toLocaleString('en-US');

const SAVED_MESSAGE = 'Note saved. Only you can see it.';
const DELETED_MESSAGE = 'Note deleted.';
const TOO_LONG_MESSAGE = `Keep your note to ${MAX_LABEL} characters or fewer. Shorten it and save again.`;
const FAILED_MESSAGE = 'We could not save your note. Check your connection and try again.';

export function StudentNoteEditor({ studentId, note, onSaved }) {
  const fieldId = React.useId();
  const hintId = React.useId();
  const [text, setText] = React.useState(note || '');
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [notice, setNotice] = React.useState(null);

  // A refreshed dashboard or another student brings a new saved note; show that one.
  React.useEffect(() => {
    setText(note || '');
  }, [note, studentId]);

  const save = async () => {
    setNotice(null);
    if (text.trim().length > NOTE_MAX_LENGTH) {
      setError(TOO_LONG_MESSAGE);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const result = await api.put(`/dashboard/instructor/students/${encodeURIComponent(studentId)}/note`, { text });
      const saved = result?.note ?? null;
      setText(saved?.text || '');
      setNotice(saved ? SAVED_MESSAGE : DELETED_MESSAGE);
      onSaved?.(saved);
    } catch (err) {
      setError(err?.data?.message || FAILED_MESSAGE);
    } finally {
      setSaving(false);
    }
  };

  const length = text.trim().length;

  return (
    <div style={{ background: palette.white, borderRadius: 20, border: '1px solid ' + palette.border, padding: 28 }}>
      <label htmlFor={fieldId} style={{ display: 'block', fontFamily: FONT, fontWeight: 700, fontSize: 15, color: palette.espresso, margin: '0 0 6px' }}>Private notes</label>
      <p id={hintId} style={{ fontFamily: FONT, fontSize: type.body, color: palette.walnut, lineHeight: 1.6, margin: '0 0 12px' }}>
        Only you can see these notes. Your student never does. Save an empty note to delete it.
      </p>
      <textarea
        id={fieldId}
        aria-describedby={hintId}
        value={text}
        onChange={(e) => { setText(e.target.value); setNotice(null); }}
        rows={4}
        placeholder="Where you left off, what to practise next…"
        style={{ width: '100%', padding: '12px 14px', borderRadius: 12, border: '1px solid ' + (length > NOTE_MAX_LENGTH ? palette.errorRed : palette.border), fontFamily: FONT, fontSize: type.body, resize: 'vertical', boxSizing: 'border-box', outline: 'none' }}
      />
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 12 }}>
        <Btn onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save notes'}</Btn>
        <span style={{ fontFamily: FONT, fontSize: type.caption, color: length > NOTE_MAX_LENGTH ? palette.errorRed : palette.driftwood }}>
          {length.toLocaleString('en-US')} / {MAX_LABEL}
        </span>
      </div>
      {error && (
        <p role="alert" style={{ fontFamily: FONT, fontSize: type.label, color: palette.errorRed, lineHeight: 1.5, margin: '10px 0 0' }}>{error}</p>
      )}
      {notice && (
        <p role="status" style={{ fontFamily: FONT, fontSize: type.label, color: palette.sageDark, lineHeight: 1.5, margin: '10px 0 0' }}>{notice}</p>
      )}
    </div>
  );
}
